<template>
    <div class="distribution_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">{{ title }}</span>
        </div>

        <div class="donut_container">
            <EChart v-if="chartReady" :option="chartOption" />
        </div>

        <ul v-if="rankedItems.length > 0" class="ranking_list">
            <li v-for="(item, idx) in rankedItems" :key="item.name"
                class="ranking_item">
                <span class="ranking_label">{{ item.name }}</span>
                <div class="ranking_bar_track">
                    <div class="ranking_bar_fill"
                        :style="{
                            width: barWidth(item.share),
                            background: palette[idx],
                        }"></div>
                </div>
                <span class="ranking_meta">
                    <span class="ranking_amount">{{ formatMoney(item.amount) }}</span>
                    <span class="ranking_dot" aria-hidden="true">·</span>
                    <span class="ranking_share">{{ formatShare(item.share) }}</span>
                </span>
            </li>
        </ul>
    </div>
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import { parseToMoney } from "@/utils/currency";
import { interpolateHexPalette } from "@/utils/colorPalette";

// Anchor tokens — leídos por getComputedStyle en mounted porque el canvas
// de ECharts no resuelve var(...). Mantener este par como los extremos del
// gradiente del que se interpolan N colores; el theme define el rango.
const LIGHTEST_ANCHOR = "--report-chart-segment-3";
const DARKEST_ANCHOR = "--report-chart-segment-1";
const SURFACE_ANCHOR = "--report-bg";

export default {
    name: "StoresDistributionCard",
    components: { EChart, PieChartIcon },
    props: {
        // Lista genérica — el ranking puede ser tiendas, marcas, categorías,
        // cualquier cosa que el backend agrupe. Cada item: { name, amount, share }.
        items: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((s) => typeof s.name === "string"),
        },
        // Singular del grouping activo. Compone el título
        // "Distribución de ventas por {categoryLabel}". El default es
        // genérico para que el componente no se rompa si el caller olvida
        // pasarlo.
        categoryLabel: { type: String, default: "categoría" },
    },
    data() {
        // chartReady arranca false porque necesitamos resolver los anchor
        // tokens vía getComputedStyle antes del primer render. Evita el flash
        // del donut sin paleta y el caso "los anchors no existen" (theme mal
        // configurado): si el palette queda vacío, no rendereamos el chart.
        return {
            chartReady: false,
            lightAnchor: "",
            darkAnchor: "",
            surfaceColor: "",
        };
    },
    computed: {
        title() {
            return `Distribución de ventas por ${this.categoryLabel}`;
        },
        rankedItems() {
            // Sort descendente por amount para que idx 0 sea el más grande.
            // El palette mapea idx 0 → color más claro, idx N-1 → más oscuro
            // (matchea el diseño: la rebanada más grande del donut lleva el
            // tono más suave, las menores se acentúan en darker teal).
            return [...this.items].sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0));
        },
        palette() {
            if (!this.lightAnchor || !this.darkAnchor) return [];
            return interpolateHexPalette(
                this.lightAnchor,
                this.darkAnchor,
                this.rankedItems.length
            );
        },
        chartOption() {
            return {
                tooltip: {
                    trigger: "item",
                    formatter: (params) => {
                        const share = Math.round(params.percent ?? 0);
                        return `<div class="echart_tooltip">
                            <strong>${params.name}</strong><br>
                            ${parseToMoney(params.value)} · ${share}%
                        </div>`;
                    },
                },
                series: [
                    {
                        type: "pie",
                        // Ring grueso (60-90%) + padAngle: 2 para los gaps
                        // limpios entre segmentos del diseño. ECharts 5.3+
                        // soporta padAngle; con 5.6 está disponible.
                        radius: ["60%", "90%"],
                        padAngle: 2,
                        avoidLabelOverlap: false,
                        label: { show: false },
                        labelLine: { show: false },
                        itemStyle: {
                            borderRadius: 0,
                        },
                        data: this.rankedItems.map((s, i) => ({
                            value: s.amount,
                            name: s.name,
                            itemStyle: { color: this.palette[i] },
                        })),
                    },
                ],
            };
        },
    },
    mounted() {
        this.resolveCssColors();
        this.chartReady = !!this.lightAnchor && !!this.darkAnchor;
    },
    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            const root = getComputedStyle(document.documentElement);
            this.lightAnchor = root.getPropertyValue(LIGHTEST_ANCHOR).trim();
            this.darkAnchor = root.getPropertyValue(DARKEST_ANCHOR).trim();
            this.surfaceColor = root.getPropertyValue(SURFACE_ANCHOR).trim();
        },
        sharePercent(share) {
            const v = Math.max(0, Math.min(1, share ?? 0));
            return Math.round(v * 100);
        },
        barWidth(share) {
            return `${this.sharePercent(share)}%`;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatShare(share) {
            return `${this.sharePercent(share)}%`;
        },
    },
};
</script>

<style scoped lang="scss">
.distribution_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 8px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

// Donut con tamaño fijo (no flex:1) — antes absorbía el sobrante y empujaba
// la lista fuera de la card. Ahora la lista es la que toma el resto y scrollea.
.donut_container {
    flex-shrink: 0;
    height: 240px;
    margin: 8px 0 16px;
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.ranking_item {
    display: grid;
    // 1ª col: label flexible (capped a ~120 para nombres largos sin reventar
    // la grid). 2ª: bar fluido. 3ª: meta (amount · share) auto-sized.
    grid-template-columns: minmax(60px, 120px) 1fr auto;
    gap: 16px;
    align-items: center;
    padding: 8px 0;
    font-size: 13px;

    + .ranking_item {
        // Sin separador visual en el diseño — pero respetando el ritmo de
        // 8px entre filas via padding del propio item.
    }
}

.ranking_label {
    color: var(--report-text);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ranking_bar_track {
    background: var(--report-progress-bg);
    border-radius: 999px;
    height: 13px;
    overflow: hidden;
}

.ranking_bar_fill {
    height: 100%;
    border-radius: 999px;
    transition: width 200ms ease-out;
}

.ranking_meta {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    color: var(--report-text-muted);
    font-size: 13px;
    white-space: nowrap;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
    }
}
</style>
